/**
 * React Query hooks for the Quote module (M-QUOTE.3.a).
 *
 * Mirrors the shape of `use-agents.ts` / `use-leads.ts`. Catalog, playbooks,
 * documents, drafts, sequences, enrollments — list queries + mutating
 * helpers that invalidate the right query keys on success.
 */
import { useAuth } from "@clerk/clerk-react";
import type {
  CreateQuoteCatalogItemRequest,
  CreateQuoteFollowupSequenceRequest,
  CreateQuotePlaybookRequest,
  CreateQuotePricingRuleRequest,
  CreateQuoteServiceAreaRequest,
  EnrollQuoteFollowupRequest,
  QuoteCatalogItemResponse,
  QuoteDocumentResponse,
  QuoteDraftResponse,
  QuoteFollowupEnrollmentResponse,
  QuoteFollowupSequenceDetailResponse,
  QuoteFollowupSequenceResponse,
  QuotePlaybookResponse,
  QuotePricingRuleResponse,
  QuoteServiceAreaResponse,
  RejectQuoteDraftRequest,
  UpdateQuoteCatalogItemRequest,
  UpdateQuotePlaybookRequest,
} from "@simpliwork/api-schema";
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { ApiError } from "@/lib/api-client";
import {
  approveQuoteDraft,
  type CatalogItemList,
  cancelQuoteEnrollment,
  createQuoteCatalogItem,
  createQuotePlaybook,
  createQuoteSequence,
  type DocumentChunkList,
  type DocumentList,
  type DraftList,
  deleteQuoteCatalogItem,
  deleteQuoteDocument,
  deleteQuotePlaybook,
  deleteQuoteSequence,
  type EnrollmentList,
  enrollQuoteFollowup,
  fetchQuoteCatalogItems,
  fetchQuoteDocument,
  fetchQuoteDocumentChunks,
  fetchQuoteDocuments,
  fetchQuoteDraftsAll,
  fetchQuoteDraftsPending,
  fetchQuoteEnrollments,
  fetchQuotePlaybooks,
  fetchQuotePricingRules,
  fetchQuoteSequence,
  fetchQuoteSequences,
  fetchQuoteServiceAreas,
  createQuotePricingRule,
  createQuoteServiceArea,
  deleteQuotePricingRule,
  deleteQuoteServiceArea,
  type PlaybookList,
  type PricingRuleList,
  rejectQuoteDraft,
  requestQuoteDraft,
  type SequenceList,
  type ServiceAreaList,
  updateQuoteCatalogItem,
  updateQuotePlaybook,
  uploadQuoteDocument,
} from "@/lib/quote-api";

export const quoteKeys = {
  all: ["quote"] as const,
  catalog: () => ["quote", "catalog"] as const,
  pricingRules: () => ["quote", "catalog", "pricing-rules"] as const,
  serviceAreas: () => ["quote", "catalog", "service-areas"] as const,
  playbooks: () => ["quote", "playbooks"] as const,
  documents: () => ["quote", "documents"] as const,
  document: (id: string) => ["quote", "documents", id] as const,
  documentChunks: (id: string) => ["quote", "documents", id, "chunks"] as const,
  draftsPending: () => ["quote", "drafts", "pending"] as const,
  draftsAll: () => ["quote", "drafts", "all"] as const,
  sequences: () => ["quote", "sequences"] as const,
  sequence: (id: string) => ["quote", "sequences", id] as const,
  enrollments: () => ["quote", "enrollments"] as const,
};

type QErr = ApiError | Error;

// ── catalog ────────────────────────────────────────────────────────────────

export function useQuoteCatalog(): UseQueryResult<CatalogItemList, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: quoteKeys.catalog(),
    queryFn: () => fetchQuoteCatalogItems({ getToken: () => getToken() }),
  });
}

export function useCreateQuoteCatalogItem(): UseMutationResult<
  QuoteCatalogItemResponse,
  QErr,
  CreateQuoteCatalogItemRequest
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createQuoteCatalogItem(body, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.catalog() }),
  });
}

export function useUpdateQuoteCatalogItem(): UseMutationResult<
  QuoteCatalogItemResponse,
  QErr,
  { id: string; body: UpdateQuoteCatalogItemRequest }
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => updateQuoteCatalogItem(id, body, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.catalog() }),
  });
}

// ── pricing rules ──────────────────────────────────────────────────────────

export function useQuotePricingRules(): UseQueryResult<PricingRuleList, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: quoteKeys.pricingRules(),
    queryFn: () => fetchQuotePricingRules({ getToken: () => getToken() }),
  });
}

export function useCreateQuotePricingRule(): UseMutationResult<
  QuotePricingRuleResponse,
  QErr,
  CreateQuotePricingRuleRequest
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createQuotePricingRule(body, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.pricingRules() }),
  });
}

export function useDeleteQuotePricingRule(): UseMutationResult<void, QErr, string> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteQuotePricingRule(id, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.pricingRules() }),
  });
}

// ── service areas ──────────────────────────────────────────────────────────

export function useQuoteServiceAreas(): UseQueryResult<ServiceAreaList, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: quoteKeys.serviceAreas(),
    queryFn: () => fetchQuoteServiceAreas({ getToken: () => getToken() }),
  });
}

export function useCreateQuoteServiceArea(): UseMutationResult<
  QuoteServiceAreaResponse,
  QErr,
  CreateQuoteServiceAreaRequest
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createQuoteServiceArea(body, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.serviceAreas() }),
  });
}

export function useDeleteQuoteServiceArea(): UseMutationResult<void, QErr, string> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteQuoteServiceArea(id, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.serviceAreas() }),
  });
}

export function useDeleteQuoteCatalogItem(): UseMutationResult<void, QErr, string> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteQuoteCatalogItem(id, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.catalog() }),
  });
}

// ── playbooks ──────────────────────────────────────────────────────────────

export function useQuotePlaybooks(): UseQueryResult<PlaybookList, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: quoteKeys.playbooks(),
    queryFn: () => fetchQuotePlaybooks({ getToken: () => getToken() }),
  });
}

export function useCreateQuotePlaybook(): UseMutationResult<
  QuotePlaybookResponse,
  QErr,
  CreateQuotePlaybookRequest
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createQuotePlaybook(body, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.playbooks() }),
  });
}

export function useUpdateQuotePlaybook(): UseMutationResult<
  QuotePlaybookResponse,
  QErr,
  { id: string; body: UpdateQuotePlaybookRequest }
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => updateQuotePlaybook(id, body, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.playbooks() }),
  });
}

export function useDeleteQuotePlaybook(): UseMutationResult<void, QErr, string> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteQuotePlaybook(id, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.playbooks() }),
  });
}

// ── documents ──────────────────────────────────────────────────────────────

// Documents transition pending → processing → ready (or → failed) via an
// Inngest function on the orchestrator. Poll while anything is still in
// flight so users see status updates without manually refreshing. Stops
// polling as soon as every row reaches a terminal state.
const DOCUMENT_POLL_INTERVAL_MS = 2500;

function anyDocumentInFlight(data: DocumentList | undefined): boolean {
  if (!data) return false;
  return data.items.some((d) => d.status === "pending" || d.status === "processing");
}

export function useQuoteDocuments(): UseQueryResult<DocumentList, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: quoteKeys.documents(),
    queryFn: () => fetchQuoteDocuments({ getToken: () => getToken() }),
    refetchInterval: (query) =>
      anyDocumentInFlight(query.state.data) ? DOCUMENT_POLL_INTERVAL_MS : false,
  });
}

// Detail-view siblings: the single-document metadata query polls on the
// same in-flight cadence so the detail page shows live status; chunks
// are fetched only once status === "ready" (lazy via `enabled`), since
// `pending`/`processing`/`failed` rows have no chunks worth showing.
export function useQuoteDocument(id: string): UseQueryResult<QuoteDocumentResponse, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: quoteKeys.document(id),
    queryFn: () => fetchQuoteDocument(id, { getToken: () => getToken() }),
    enabled: id.length > 0,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return data.status === "pending" || data.status === "processing"
        ? DOCUMENT_POLL_INTERVAL_MS
        : false;
    },
  });
}

export function useQuoteDocumentChunks(
  id: string,
  opts: { enabled?: boolean } = {},
): UseQueryResult<DocumentChunkList, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: quoteKeys.documentChunks(id),
    queryFn: () => fetchQuoteDocumentChunks(id, { getToken: () => getToken() }),
    enabled: id.length > 0 && (opts.enabled ?? true),
  });
}

export function useUploadQuoteDocument(): UseMutationResult<
  QuoteDocumentResponse,
  QErr,
  { file: File; documentType?: string }
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, documentType }) =>
      uploadQuoteDocument(file, documentType ?? null, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.documents() }),
  });
}

export function useDeleteQuoteDocument(): UseMutationResult<void, QErr, string> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteQuoteDocument(id, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.documents() }),
  });
}

// ── drafts ─────────────────────────────────────────────────────────────────

export function useQuoteDraftsPending(): UseQueryResult<DraftList, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: quoteKeys.draftsPending(),
    queryFn: () => fetchQuoteDraftsPending({ getToken: () => getToken() }),
  });
}

export function useQuoteDraftsAll(): UseQueryResult<DraftList, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: quoteKeys.draftsAll(),
    queryFn: () => fetchQuoteDraftsAll({ getToken: () => getToken() }),
  });
}

export function useApproveQuoteDraft(): UseMutationResult<QuoteDraftResponse, QErr, string> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => approveQuoteDraft(id, { getToken: () => getToken() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: quoteKeys.draftsPending() });
      qc.invalidateQueries({ queryKey: quoteKeys.draftsAll() });
    },
  });
}

export function useRejectQuoteDraft(): UseMutationResult<
  QuoteDraftResponse,
  QErr,
  { id: string; body: RejectQuoteDraftRequest }
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => rejectQuoteDraft(id, body, { getToken: () => getToken() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: quoteKeys.draftsPending() });
      qc.invalidateQueries({ queryKey: quoteKeys.draftsAll() });
    },
  });
}

// ── sequences + enrollments ───────────────────────────────────────────────

export function useQuoteSequences(): UseQueryResult<SequenceList, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: quoteKeys.sequences(),
    queryFn: () => fetchQuoteSequences({ getToken: () => getToken() }),
  });
}

export function useQuoteSequence(
  id: string,
): UseQueryResult<QuoteFollowupSequenceDetailResponse, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: quoteKeys.sequence(id),
    queryFn: () => fetchQuoteSequence(id, { getToken: () => getToken() }),
    enabled: id.length > 0,
  });
}

export function useCreateQuoteSequence(): UseMutationResult<
  QuoteFollowupSequenceResponse,
  QErr,
  CreateQuoteFollowupSequenceRequest
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createQuoteSequence(body, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.sequences() }),
  });
}

export function useDeleteQuoteSequence(): UseMutationResult<void, QErr, string> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteQuoteSequence(id, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.sequences() }),
  });
}

export function useQuoteEnrollments(): UseQueryResult<EnrollmentList, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: quoteKeys.enrollments(),
    queryFn: () => fetchQuoteEnrollments({ getToken: () => getToken() }),
  });
}

export function useEnrollQuoteFollowup(): UseMutationResult<
  QuoteFollowupEnrollmentResponse,
  QErr,
  EnrollQuoteFollowupRequest
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => enrollQuoteFollowup(body, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.enrollments() }),
  });
}

export function useCancelQuoteEnrollment(): UseMutationResult<
  QuoteFollowupEnrollmentResponse,
  QErr,
  string
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => cancelQuoteEnrollment(id, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKeys.enrollments() }),
  });
}

export function useRequestQuoteDraft(): UseMutationResult<
  { enqueued: true },
  QErr,
  { lead_id: string; workflow?: "draft_first_response" | "enrich" }
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => requestQuoteDraft(body, { getToken: () => getToken() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: quoteKeys.draftsPending() });
      qc.invalidateQueries({ queryKey: quoteKeys.draftsAll() });
    },
  });
}
